// src/pages/.../VoteResultsModal.jsx

import React, { useEffect, useMemo, useState } from "react";

import {
  getVoteResults,
  getVoteResultsDetailed,
} from "../../../../services/voteServices";

import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import {
  FaChartBar,
  FaChartPie,
  FaCheckCircle,
  FaListUl,
  FaUser,
  FaUsers,
  FaVoteYea,
} from "react-icons/fa";

import dayjs from "dayjs";
import { toast } from "react-toastify";

import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../../../components/Modal/Modal";

const COLORS = [
  "#2563EB",
  "#F59E0B",
  "#16A34A",
  "#EF4444",
  "#9333EA",
  "#0891B2",
  "#DB2777",
  "#EA580C",
  "#4F46E5",
  "#0D9488",
  "#65A30D",
  "#C026D3",
  "#0284C7",
  "#E11D48",
  "#7C3AED",
  "#059669",
];

const cx = (...classes) => classes.filter(Boolean).join(" ");

const toNumber = (value) => {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR").format(toNumber(value));

const formatPercentage = (value) => {
  const numericValue = toNumber(value);

  if (numericValue === 0) {
    return "0 %";
  }

  if (numericValue < 1) {
    return `${numericValue.toFixed(1).replace(".", ",")} %`;
  }

  return `${Math.round(numericValue)} %`;
};

const stripHtml = (html = "") => {
  if (typeof document === "undefined") {
    return html.replace(/<[^>]*>/g, " ");
  }

  const element = document.createElement("div");
  element.innerHTML = html;

  return element.textContent || element.innerText || "";
};

const normalizeChoices = (choices = []) => {
  if (!Array.isArray(choices)) {
    return [];
  }

  return choices.map((choice, index) => ({
    id:
      choice?.choice_id ??
      choice?.id ??
      `${choice?.choice_text || "choice"}-${index}`,
    label:
      choice?.choice_text ||
      choice?.text ||
      choice?.label ||
      `Choix ${index + 1}`,
    count: toNumber(choice?.count),
    color: COLORS[index % COLORS.length],
  }));
};

const getExplicitResponseTotal = (question) => {
  const possibleValues = [
    question?.total_responses,
    question?.response_count,
    question?.responses_count,
    question?.ballots_count,
    question?.voters_count,
    question?.total_voters,
  ];

  const foundValue = possibleValues.find(
    (value) => value != null && Number.isFinite(Number(value)),
  );

  return foundValue == null ? null : toNumber(foundValue);
};

const getQuestionStatistics = (question, choices) => {
  const selectionTotal = choices.reduce(
    (total, choice) => total + choice.count,
    0,
  );

  const explicitResponseTotal = getExplicitResponseTotal(question);

  const percentageBase =
    question?.type === "multiple" &&
    explicitResponseTotal != null &&
    explicitResponseTotal > 0
      ? explicitResponseTotal
      : selectionTotal;

  const totalDisplayed =
    question?.type === "multiple" &&
    explicitResponseTotal != null
      ? explicitResponseTotal
      : selectionTotal;

  const totalLabel =
    question?.type === "multiple" &&
    explicitResponseTotal != null
      ? "répondants"
      : question?.type === "multiple"
        ? "sélections"
        : "votes exprimés";

  const percentageLabel =
    question?.type === "multiple" &&
    explicitResponseTotal != null
      ? "des répondants"
      : question?.type === "multiple"
        ? "des sélections"
        : "des votes";

  const leadingChoice = [...choices].sort(
    (firstChoice, secondChoice) =>
      secondChoice.count - firstChoice.count,
  )[0];

  return {
    selectionTotal,
    percentageBase,
    totalDisplayed,
    totalLabel,
    percentageLabel,
    leadingChoice:
      leadingChoice?.count > 0 ? leadingChoice : null,
  };
};

function EmptyChartState() {
  return (
    <div className="flex min-h-[220px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50/80 px-6 text-center dark:border-slate-700 dark:bg-slate-950/40">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
        <FaChartPie className="text-xl" />
      </div>

      <div className="mt-4 text-sm font-black text-slate-700 dark:text-slate-200">
        Aucune réponse exprimée
      </div>

      <p className="mt-1 max-w-xs text-xs leading-relaxed text-slate-500 dark:text-slate-400">
        Le graphique apparaîtra dès qu’une première réponse sera enregistrée.
      </p>
    </div>
  );
}

function ChartTooltip({ active, payload, total }) {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0]?.payload;
  const percentage =
    total > 0 ? (toNumber(item?.count) / total) * 100 : 0;

  return (
    <div className="min-w-[180px] rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-2xl backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
      <div className="flex items-start gap-2">
        <span
          className="mt-1 h-3 w-3 shrink-0 rounded-full"
          style={{ backgroundColor: item?.color }}
        />

        <div className="min-w-0">
          <div className="text-sm font-black leading-snug text-slate-900 dark:text-white">
            {item?.label}
          </div>

          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {formatNumber(item?.count)} vote
            {toNumber(item?.count) > 1 ? "s" : ""}
            {" · "}
            {formatPercentage(percentage)}
          </div>
        </div>
      </div>
    </div>
  );
}

function DonutChart({ choices, total }) {
  const positiveChoices = useMemo(
    () => choices.filter((choice) => choice.count > 0),
    [choices],
  );

  if (total <= 0 || positiveChoices.length === 0) {
    return <EmptyChartState />;
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(280px,0.85fr)_minmax(280px,1.15fr)] lg:items-center">
      <div className="relative mx-auto h-[290px] w-full max-w-[360px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={positiveChoices}
              dataKey="count"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={72}
              outerRadius={110}
              paddingAngle={3}
              cornerRadius={7}
              stroke="none"
              isAnimationActive
              animationBegin={100}
              animationDuration={900}
              animationEasing="ease-out"
            >
              {positiveChoices.map((choice) => (
                <Cell
                  key={choice.id}
                  fill={choice.color}
                  className="cursor-pointer outline-none transition-opacity hover:opacity-80"
                />
              ))}
            </Pie>

            <Tooltip
              cursor={false}
              content={<ChartTooltip total={total} />}
            />
          </PieChart>
        </ResponsiveContainer>

        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-3xl font-black tracking-tight text-slate-950 dark:text-white">
            {formatNumber(total)}
          </div>

          <div className="mt-1 text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">
            vote{total > 1 ? "s" : ""}
          </div>
        </div>
      </div>

      <div className="max-h-[270px] space-y-2 overflow-y-auto pr-1">
        {positiveChoices.map((choice) => {
          const percentage =
            total > 0 ? (choice.count / total) * 100 : 0;

          return (
            <div
              key={choice.id}
              className="flex items-center gap-3 rounded-xl border border-slate-200/80 bg-white px-3 py-2.5 shadow-sm transition hover:border-slate-300 hover:shadow-md dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600"
            >
              <span
                className="h-3.5 w-3.5 shrink-0 rounded-full ring-4 ring-current/10"
                style={{ backgroundColor: choice.color }}
              />

              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold leading-snug text-slate-700 dark:text-slate-200">
                  {choice.label}
                </div>
              </div>

              <div className="shrink-0 text-right">
                <div className="text-sm font-black text-slate-900 dark:text-white">
                  {formatNumber(choice.count)}
                </div>

                <div className="text-[11px] font-bold text-slate-400">
                  {formatPercentage(percentage)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DistributionBars({
  choices,
  percentageBase,
  percentageLabel,
}) {
  const [animationStarted, setAnimationStarted] =
    useState(false);

  const maxCount = useMemo(
    () =>
      Math.max(
        0,
        ...choices.map((choice) => toNumber(choice.count)),
      ),
    [choices],
  );

  useEffect(() => {
    setAnimationStarted(false);

    const animationFrame = window.requestAnimationFrame(() => {
      setAnimationStarted(true);
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [choices]);

  if (!choices.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-400">
        Aucun choix configuré pour cette question.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {choices.map((choice, index) => {
        const relativeWidth =
          maxCount > 0
            ? (choice.count / maxCount) * 100
            : 0;

        const percentage =
          percentageBase > 0
            ? (choice.count / percentageBase) * 100
            : 0;

        const displayedWidth =
          choice.count > 0
            ? Math.max(relativeWidth, 2.5)
            : 0;

        return (
          <div
            key={choice.id}
            className="group rounded-2xl border border-slate-200/80 bg-white p-3 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600"
          >
            <div className="flex items-start gap-3">
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-xs font-black"
                style={{
                  backgroundColor: `${choice.color}18`,
                  color: choice.color,
                }}
              >
                {index + 1}
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm font-bold leading-snug text-slate-800 dark:text-slate-100">
                    {choice.label}
                  </div>

                  <div className="shrink-0 text-right">
                    <div className="text-sm font-black text-slate-950 dark:text-white">
                      {formatNumber(choice.count)}
                    </div>

                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                      vote{choice.count > 1 ? "s" : ""}
                    </div>
                  </div>
                </div>

                <div className="mt-2.5 flex items-center gap-3">
                  <div className="relative h-3 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200/80 dark:bg-slate-800 dark:ring-slate-700">
                    <div
                      className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-1000 ease-out"
                      style={{
                        width: animationStarted
                          ? `${displayedWidth}%`
                          : "0%",
                        background: `linear-gradient(90deg, ${choice.color}, ${choice.color}CC)`,
                      }}
                    />

                    {choice.count === 0 && (
                      <div className="absolute inset-0 flex items-center px-2">
                        <div className="h-px w-full border-t border-dashed border-slate-300 dark:border-slate-600" />
                      </div>
                    )}
                  </div>

                  <div className="w-[58px] shrink-0 text-right text-xs font-black text-slate-600 dark:text-slate-300">
                    {formatPercentage(percentage)}
                  </div>
                </div>

                <div className="mt-1 text-[10px] text-slate-400">
                  {percentageLabel}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function QuestionSummary({
  question,
  choices,
  statistics,
}) {
  const choiceCount = choices.length;

  return (
    <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-3">
      <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-3 dark:border-blue-500/20 dark:bg-blue-500/10">
        <div className="flex items-center gap-2 text-blue-600 dark:text-blue-300">
          <FaUsers className="text-sm" />

          <span className="text-[10px] font-black uppercase tracking-[0.13em]">
            Participation
          </span>
        </div>

        <div className="mt-2 text-xl font-black text-slate-950 dark:text-white">
          {formatNumber(statistics.totalDisplayed)}
        </div>

        <div className="text-xs text-slate-500 dark:text-slate-400">
          {statistics.totalLabel}
        </div>
      </div>

      <div className="rounded-2xl border border-violet-100 bg-violet-50/70 p-3 dark:border-violet-500/20 dark:bg-violet-500/10">
        <div className="flex items-center gap-2 text-violet-600 dark:text-violet-300">
          <FaListUl className="text-sm" />

          <span className="text-[10px] font-black uppercase tracking-[0.13em]">
            Possibilités
          </span>
        </div>

        <div className="mt-2 text-xl font-black text-slate-950 dark:text-white">
          {formatNumber(choiceCount)}
        </div>

        <div className="text-xs text-slate-500 dark:text-slate-400">
          choix proposé{choiceCount > 1 ? "s" : ""}
        </div>
      </div>

      <div className="col-span-2 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-3 dark:border-emerald-500/20 dark:bg-emerald-500/10 lg:col-span-1">
        <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300">
          <FaCheckCircle className="text-sm" />

          <span className="text-[10px] font-black uppercase tracking-[0.13em]">
            Choix principal
          </span>
        </div>

        {statistics.leadingChoice ? (
          <>
            <div className="mt-2 line-clamp-1 text-sm font-black text-slate-950 dark:text-white">
              {statistics.leadingChoice.label}
            </div>

            <div className="text-xs text-slate-500 dark:text-slate-400">
              {formatNumber(statistics.leadingChoice.count)} réponse
              {statistics.leadingChoice.count > 1 ? "s" : ""}
            </div>
          </>
        ) : (
          <>
            <div className="mt-2 text-sm font-black text-slate-500 dark:text-slate-400">
              Aucun
            </div>

            <div className="text-xs text-slate-400">
              Pas encore de réponse
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function QuestionTypeBadge({ type }) {
  const configuration = {
    single: {
      label: "Choix unique",
      icon: FaChartPie,
      classes:
        "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/20",
    },
    multiple: {
      label: "Choix multiple",
      icon: FaChartBar,
      classes:
        "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20",
    },
    open: {
      label: "Réponse libre",
      icon: FaListUl,
      classes:
        "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
    },
  };

  const selectedConfiguration =
    configuration[type] || configuration.open;

  const Icon = selectedConfiguration.icon;

  return (
    <span
      className={cx(
        "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-black uppercase tracking-wide ring-1",
        selectedConfiguration.classes,
      )}
    >
      <Icon />

      {selectedConfiguration.label}
    </span>
  );
}

function OpenResponses({ responses = [] }) {
  const safeResponses = Array.isArray(responses)
    ? responses.filter(
        (response) =>
          typeof response === "string" &&
          response.trim().length > 0,
      )
    : [];

  if (!safeResponses.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm italic text-slate-500 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-400">
        Aucune réponse libre enregistrée.
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {safeResponses.map((response, index) => (
        <article
          key={`${response}-${index}`}
          className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-slate-950/50"
        >
          <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-blue-500 to-violet-500" />

          <div className="text-3xl font-black leading-none text-blue-200 dark:text-blue-500/30">
            “
          </div>

          <p className="-mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-700 dark:text-slate-200">
            {response}
          </p>
        </article>
      ))}
    </div>
  );
}

function GlobalQuestionCard({ question, index }) {
  const choices = useMemo(
    () => normalizeChoices(question?.choices),
    [question?.choices],
  );

  const statistics = useMemo(
    () => getQuestionStatistics(question, choices),
    [question, choices],
  );

  return (
    <article className="mb-7 overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_18px_50px_-36px_rgba(15,23,42,0.45)] dark:border-slate-700 dark:bg-slate-900">
      <div className="h-1 bg-gradient-to-r from-blue-600 via-violet-500 to-fuchsia-500" />

      <div className="p-4 sm:p-6">
        <header className="mb-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-black text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              Question {index + 1}
            </div>

            <QuestionTypeBadge type={question?.type} />
          </div>

          <div
            className="mt-4 text-base font-black leading-relaxed text-slate-900 dark:text-white sm:text-lg"
            dangerouslySetInnerHTML={{
              __html: question?.question_text || "",
            }}
          />
        </header>

        {(question?.type === "single" ||
          question?.type === "multiple") && (
          <>
            <QuestionSummary
              question={question}
              choices={choices}
              statistics={statistics}
            />

            {question?.type === "single" && (
              <div className="mb-7 rounded-3xl border border-slate-200/80 bg-slate-50/60 p-3 sm:p-5 dark:border-slate-700 dark:bg-slate-950/30">
                <div className="mb-3 flex items-center gap-2 text-sm font-black text-slate-700 dark:text-slate-200">
                  <FaChartPie className="text-blue-600 dark:text-blue-400" />
                  Répartition des réponses
                </div>

                <DonutChart
                  choices={choices}
                  total={statistics.selectionTotal}
                />
              </div>
            )}

            <div>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-black text-slate-700 dark:text-slate-200">
                  <FaChartBar className="text-violet-600 dark:text-violet-400" />

                  {question?.type === "single"
                    ? "Détail de tous les choix"
                    : "Histogramme des sélections"}
                </div>

                <span className="text-[11px] font-bold text-slate-400">
                  Tous les choix sont affichés, y compris ceux à 0
                </span>
              </div>

              <DistributionBars
                choices={choices}
                percentageBase={statistics.percentageBase}
                percentageLabel={statistics.percentageLabel}
              />
            </div>
          </>
        )}

        {question?.type === "open" && (
          <OpenResponses responses={question?.responses} />
        )}
      </div>
    </article>
  );
}

const getDetailedResponseLabel = (question, answer) => {
  if (question?.type === "open") {
    return answer?.answer_text || "—";
  }

  if (Array.isArray(answer?.choices)) {
    const labels = answer.choices
      .map(
        (choice) =>
          choice?.text ||
          choice?.choice_text ||
          choice?.label,
      )
      .filter(Boolean);

    return labels.length ? labels.join(", ") : "—";
  }

  return (
    answer?.choice?.text ||
    answer?.choice?.choice_text ||
    answer?.choice?.label ||
    answer?.answer_text ||
    "—"
  );
};

function DetailedQuestionCard({ question, index }) {
  return (
    <article className="mb-7 overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="h-1 bg-gradient-to-r from-indigo-600 to-violet-500" />

      <div className="p-4 sm:p-6">
        <header className="mb-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-black text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              Question {index + 1}
            </span>

            <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1.5 text-[11px] font-black uppercase tracking-wide text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
              <FaUser />
              Vue nominative
            </span>
          </div>

          <div
            className="mt-4 text-base font-black leading-relaxed text-slate-900 dark:text-white sm:text-lg"
            dangerouslySetInnerHTML={{
              __html: question?.question_text || "",
            }}
          />
        </header>

        {question?.answers?.length ? (
          <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700">
            <div className="max-h-[430px] overflow-auto">
              <table className="min-w-full text-xs sm:text-sm">
                <thead className="sticky top-0 z-10 bg-slate-100/95 backdrop-blur dark:bg-slate-800/95">
                  <tr>
                    <th className="whitespace-nowrap px-4 py-3 text-left font-black text-slate-600 dark:text-slate-300">
                      Votant
                    </th>

                    <th className="min-w-[240px] px-4 py-3 text-left font-black text-slate-600 dark:text-slate-300">
                      Réponse
                    </th>

                    <th className="whitespace-nowrap px-4 py-3 text-left font-black text-slate-600 dark:text-slate-300">
                      Date / heure
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-700 dark:bg-slate-900">
                  {question.answers.map((answer, answerIndex) => {
                    const answerUser = answer?.user || {};

                    const userLabel =
                      `${answerUser?.prenom || ""} ${
                        answerUser?.nom || ""
                      }`.trim() ||
                      (answerUser?.id
                        ? `Utilisateur #${answerUser.id}`
                        : "Utilisateur inconnu");

                    const responseLabel =
                      getDetailedResponseLabel(
                        question,
                        answer,
                      );

                    const dateLabel = answer?.timestamp
                      ? dayjs(answer.timestamp).format(
                          "DD/MM/YYYY à HH:mm",
                        )
                      : "—";

                    return (
                      <tr
                        key={
                          answer?.ballot_id ||
                          `${question.question_id}-${answerIndex}`
                        }
                        className="transition hover:bg-slate-50 dark:hover:bg-slate-800/70"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-100 font-black text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300">
                              {userLabel
                                .split(" ")
                                .filter(Boolean)
                                .slice(0, 2)
                                .map((word) => word.charAt(0))
                                .join("")
                                .toUpperCase() || "?"}
                            </span>

                            <span className="font-bold text-slate-800 dark:text-slate-100">
                              {userLabel}
                            </span>
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <span className="inline-flex rounded-xl bg-slate-100 px-3 py-2 font-medium leading-relaxed text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                            {responseLabel}
                          </span>
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-slate-500 dark:text-slate-400">
                          {dateLabel}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm italic text-slate-500 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-400">
            Aucune réponse pour cette question.
          </div>
        )}
      </div>
    </article>
  );
}

function LoadingResults() {
  return (
    <div className="space-y-5">
      {[1, 2].map((item) => (
        <div
          key={item}
          className="animate-pulse rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900"
        >
          <div className="flex justify-between gap-4">
            <div className="h-7 w-24 rounded-full bg-slate-200 dark:bg-slate-700" />
            <div className="h-7 w-32 rounded-full bg-slate-200 dark:bg-slate-700" />
          </div>

          <div className="mt-5 h-5 w-2/3 rounded bg-slate-200 dark:bg-slate-700" />

          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {[1, 2, 3].map((card) => (
              <div
                key={card}
                className="h-24 rounded-2xl bg-slate-100 dark:bg-slate-800"
              />
            ))}
          </div>

          <div className="mt-5 h-64 rounded-3xl bg-slate-100 dark:bg-slate-800" />
        </div>
      ))}
    </div>
  );
}

const VoteResultsModal = ({
  isOpen,
  onClose,
  voteId,
  voteTitle,
  isAnonymous,
}) => {
  const [results, setResults] = useState([]);
  const [detailed, setDetailed] = useState(null);
  const [viewMode, setViewMode] = useState("global");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !voteId) {
      setResults([]);
      setDetailed(null);
      setViewMode("global");
      setLoading(false);

      return undefined;
    }

    let isActive = true;

    const fetchResults = async () => {
      setLoading(true);
      setViewMode("global");

      try {
        const globalData = await getVoteResults(voteId);

        if (!isActive) {
          return;
        }

        setResults(
          Array.isArray(globalData?.results)
            ? globalData.results
            : [],
        );

        if (!isAnonymous) {
          const detailedData =
            await getVoteResultsDetailed(voteId);

          if (!isActive) {
            return;
          }

          setDetailed(detailedData || null);
        } else {
          setDetailed(null);
        }
      } catch (error) {
        console.error(
          "Erreur lors du chargement des résultats :",
          error,
        );

        if (isActive) {
          setResults([]);
          setDetailed(null);

          toast.error(
            "Erreur lors du chargement des résultats.",
          );
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchResults();

    return () => {
      isActive = false;
    };
  }, [isOpen, voteId, isAnonymous]);

  if (!isOpen) {
    return null;
  }

  const subtitle = isAnonymous
    ? "Ce vote est anonyme. Seules les statistiques globales sont accessibles."
    : "Ce vote est nominatif. Consultez la synthèse ou le détail des réponses par votant.";

  const title =
    voteTitle || `Vote #${voteId}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      labelledById="vote-results-title"
      describedById="vote-results-desc"
    >
      <ModalHeader
        id="vote-results-title"
        title={`Résultats — ${title}`}
        subtitle={subtitle}
        onClose={onClose}
      />

      <ModalBody id="vote-results-desc">
        {!isAnonymous && detailed && (
          <div className="sticky top-0 z-20 mb-5 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur dark:border-slate-700 dark:bg-slate-900/95 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                Mode d’affichage
              </div>

              <div className="mt-0.5 text-sm font-bold text-slate-700 dark:text-slate-200">
                {viewMode === "global"
                  ? "Statistiques générales"
                  : "Réponses nominatives"}
              </div>
            </div>

            <div className="grid grid-cols-2 rounded-xl bg-slate-100 p-1 dark:bg-slate-800">
              <button
                type="button"
                onClick={() => setViewMode("global")}
                className={cx(
                  "inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-xs font-black transition",
                  viewMode === "global"
                    ? "bg-white text-slate-900 shadow-md dark:bg-slate-950 dark:text-white"
                    : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white",
                )}
              >
                <FaChartPie />
                Synthèse
              </button>

              <button
                type="button"
                onClick={() => setViewMode("detailed")}
                className={cx(
                  "inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-xs font-black transition",
                  viewMode === "detailed"
                    ? "bg-white text-slate-900 shadow-md dark:bg-slate-950 dark:text-white"
                    : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white",
                )}
              >
                <FaUsers />
                Par votant
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <LoadingResults />
        ) : (
          <>
            {viewMode === "global" && (
              <>
                {results.length > 0 ? (
                  results.map((question, index) => (
                    <GlobalQuestionCard
                      key={
                        question?.question_id ||
                        `question-${index}`
                      }
                      question={question}
                      index={index}
                    />
                  ))
                ) : (
                  <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center dark:border-slate-700 dark:bg-slate-900">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                      <FaVoteYea className="text-2xl" />
                    </div>

                    <div className="mt-4 font-black text-slate-700 dark:text-slate-200">
                      Aucun résultat à afficher
                    </div>

                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Aucune réponse n’a encore été enregistrée pour ce vote.
                    </p>
                  </div>
                )}
              </>
            )}

            {viewMode === "detailed" && !isAnonymous && (
              <>
                {detailed?.questions?.length ? (
                  detailed.questions.map(
                    (question, index) => (
                      <DetailedQuestionCard
                        key={
                          question?.question_id ||
                          `detailed-question-${index}`
                        }
                        question={question}
                        index={index}
                      />
                    ),
                  )
                ) : (
                  <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center dark:border-slate-700 dark:bg-slate-900">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                      <FaUser className="text-xl" />
                    </div>

                    <div className="mt-4 font-black text-slate-700 dark:text-slate-200">
                      Aucune donnée nominative
                    </div>

                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Aucune réponse individuelle n’est disponible pour ce vote.
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-10 items-center justify-center rounded-xl bg-slate-100 px-5 text-sm font-black text-slate-800 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700"
        >
          Fermer
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default VoteResultsModal;